const debounce = (func, wait) => {
  let timeout
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout)
      func(...args)
    }
    clearTimeout(timeout)
    timeout = setTimeout(later, wait)
  }
}

const retryQueue = new Set()

// Restore session from cloud
export const restoreSession = async (sessionId) => {
  if (!sessionId) {
    console.error('🚫 restoreSession: No sessionId provided');
    return null;
  }

  console.log('📡 Calling API: /api/sessions/restore?sessionId=', sessionId);

  try {
    const response = await fetch(`/api/sessions/restore?sessionId=${sessionId}`)
    console.log('📡 API Response Status:', response.status, response.statusText);

    const result = await response.json()
    console.log('📡 API Response Body:', result);

    if (!response.ok || !result.success) {
      if (response.status === 404) {
        console.log('ℹ️ No cloud backup found for session (404)');
      } else {
        console.error('❌ Failed to restore session:', result.error);
      }
      return null
    }

    console.log(`✅ Session restored from ${result.source}`);
    console.log('📦 Restored data:', result.data);
    return result.data
  } catch (error) {
    console.error('❌ Exception in restoreSession:', error);
    return null
  }
}

const cloudBackup = async (sessionId, data) => {
  const maxRetries = 3
  let attempt = 0
  
  while (attempt < maxRetries) {
    try {
      const response = await fetch('/api/sessions/backup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId,
          data: JSON.parse(data)
        })
      })
      
      const result = await response.json()
      
      // Handle rate limiting
      if (response.status === 429) {
        const retryAfter = result.retry_after || 60
        console.warn(`Rate limited. Retrying after ${retryAfter} seconds`)
        await new Promise(resolve => setTimeout(resolve, retryAfter * 1000))
        continue
      }
      
      // Handle other errors
      if (!response.ok || !result.success) {
        throw new Error(result.error || `HTTP error! status: ${response.status}`)
      }
      
      console.log(`Session backed up successfully. Count: ${result.backup_count}`)
      return true
    } catch (error) {
      attempt++
      if (attempt === maxRetries) {
        console.warn('Cloud backup failed after retries:', error)
        return false
      }
      // Exponential backoff
      await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt) * 1000))
    }
  }
  return false
}

const queueCloudBackup = debounce((sessionId, name, value) => {
  if (!sessionId || !navigator.onLine) return
  
  cloudBackup(sessionId, value).catch(error => {
    console.warn('Cloud backup error:', error)
    retryQueue.add({ sessionId, name, value, timestamp: Date.now() })
  })
}, 2000) // 2 seconds debounce as recommended

// Retry failed backups when online
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    retryQueue.forEach(async (item) => {
      if (Date.now() - item.timestamp < 3600000) { // 1 hour
        const success = await cloudBackup(item.sessionId, item.value)
        if (success) {
          retryQueue.delete(item)
        }
      } else {
        retryQueue.delete(item) // Too old, discard
      }
    })
  })
}

/**
 * Create a hybrid storage adapter for Zustand persist.
 * Saves to localStorage immediately and queues async cloud backup.
 *
 * @param {string} locale - The locale code (e.g., 'en-us') for debugging
 * @returns {Object} Storage adapter with getItem, setItem, removeItem
 */
export const createHybridStorage = (locale = 'en-us') => ({
  getItem: (name) => {
    const item = localStorage.getItem(name)
    return item
  },

  setItem: (name, value) => {
    // Save locally first (instant)
    localStorage.setItem(name, value)

    // Get session ID from the stored data
    try {
      const data = JSON.parse(value)
      // Zustand persist stores data with state and version
      const sessionId = data.state?.sessionId

      if (sessionId) {
        // Queue cloud backup (non-blocking)
        // Session ID now includes locale prefix (e.g., 'en-us-1234567890-uuid')
        queueCloudBackup(sessionId, name, value)
      }
    } catch (error) {
      // Ignore parsing errors during initialization
      if (value !== '{}') {
        console.warn(`[${locale}] Error parsing state for cloud backup:`, error)
      }
    }
  },

  removeItem: (name) => {
    localStorage.removeItem(name)
  }
})